import { useEffect, useState } from "react";
import { useSalesforce } from "../hooks/useSalesforce";
import {
  FieldType,
  FilterCriteria,
  GROUPABLE_FIELD_TYPES,
  GroupByCriteria,
  IncidentQueryField,
  OrderByCriteria,
} from "../types/salesforce";
import { Switch } from "./UI/switch";
import { Label } from "./UI/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./UI/select";
import { DateInput } from "../QueryBuilder/DateInput";
import { Input } from "./UI/input";
import { Plus, X } from "lucide-react";
import { OrderBySelector } from "../QueryBuilder/OrderBySelector";
import { GroupBySelector } from "../QueryBuilder/GroupBySelector";
import FilterSectionModal from "./FilterSectionModal";

// Utility functions
const isPicklistField = (field: IncidentQueryField): boolean => {
  return (
    field.Field_Type__c === "Picklist" ||
    field.Field_Type__c === "MultiselectPicklist" ||
    field.Treat_as_Picklist__c === true
  );
};

const getValuesFromField = (field: IncidentQueryField): string[] => {
  if (field.Values__c) {
    return field.Values__c.split("\n")
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }
  return [];
};

// Custom hook for managing filters
const useFilterManagement = (
  setFilters: (filters: FilterCriteria[]) => void,
  setLogicExpression: (expression: string | null) => void
) => {
  const [localFilters, setLocalFilters] = useState<
    (FilterCriteria & { id: string })[]
  >([]);

  // Update parent filters when local filters change
  useEffect(() => {
    setFilters(localFilters.map(({ id, ...filter }) => filter));
  }, [localFilters, setFilters]);

  // Add IDs to initial filters
  useEffect(() => {
    if (localFilters.length > 0) {
      setLocalFilters(
        localFilters.map((filter) => ({
          ...filter,
          id: filter.id || crypto.randomUUID(),
        }))
      );
    }
  }, []); // Run only once on mount

  const addFilter = () => {
    const newFilter = {
      id: crypto.randomUUID(),
      fieldName: "",
      operator: "=",
      value: "",
    };
    setLocalFilters((prev) => [...prev, newFilter]);
  };

  const removeFilter = (id: string) => {
    setLocalFilters((prev) => prev.filter((filter) => filter.id !== id));
    const updatedFilters = localFilters.filter((filter) => filter.id !== id);
    if (updatedFilters.length === 0) {
      setLogicExpression(null);
    }
  };

  const updateFilter = (id: string, updates: Partial<FilterCriteria>) => {
    setLocalFilters((prev) =>
      prev.map((filter) =>
        filter.id === id ? { ...filter, ...updates } : filter
      )
    );
  };

  return { localFilters, addFilter, removeFilter, updateFilter };
};

// Picklist component
const PicklistFilterInput = ({
  filter,
  values,
  onUpdate,
  onRemove,
}: {
  filter: FilterCriteria & { id: string };
  values: string[];
  onUpdate: (value: string) => void;
  onRemove: (value: string) => void;
}) => {
  const selectedValues = Array.isArray(filter.value) ? filter.value : [];

  return (
    <div className="flex flex-col space-y-2">
      {/* <div className="flex h-10 flex-col gap-2"> */}
      {/* <div> */}
      <Select value="" onValueChange={onUpdate}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select values..." />
        </SelectTrigger>
        <SelectContent>
          {values
            .filter((value) => !selectedValues.includes(value))
            .map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Filter input component
export const FilterInput = ({
  filter,
  selectedField,
  picklistValues,
  onUpdate,
  onPicklistValueAdd,
  onPicklistValueRemove,
}: {
  filter: FilterCriteria & { id: string };
  selectedField: IncidentQueryField;
  picklistValues: Record<string, string[]>;
  onUpdate: (updates: Partial<FilterCriteria>) => void;
  onPicklistValueAdd: (value: string) => void;
  onPicklistValueRemove: (value: string) => void;
}) => {
  if (selectedField.Field_Type__c === "Checkbox") {
    return (
      <div className="flex items-center space-x-2">
        <Switch
          checked={filter.value === "true"}
          onCheckedChange={(checked) => onUpdate({ value: checked })}
          aria-label={`${selectedField.Label} value`}
        />
        <Label>{filter.value === "true" ? "True" : "False"}</Label>
      </div>
    );
  }

  if (isPicklistField(selectedField)) {
    return (
      <PicklistFilterInput
        filter={filter}
        values={picklistValues[filter.fieldName] || []}
        onUpdate={onPicklistValueAdd}
        onRemove={onPicklistValueRemove}
      />
    );
  }

  if (
    selectedField.Field_Type__c === "Date" ||
    selectedField.Field_Type__c === "DateTime"
  ) {
    return (
      <DateInput
        value={filter.value}
        onChange={(value) => onUpdate({ value })}
        field={selectedField}
        operator={filter.operator}
      />
    );
  }

  return (
    <Input
      type={selectedField.Field_Type__c === "Number" ? "number" : "text"}
      value={filter.value}
      onChange={(e) => onUpdate({ value: e.target.value })}
    />
  );
};

interface FilterSectionProps {
  availableFields: IncidentQueryField[];
  selectedFields: string[];
  orderBy: OrderByCriteria | null;
  onOrderByChange: (orderBy: OrderByCriteria | null) => void;
  groupBy: GroupByCriteria[];
  onGroupByChange: (groupBy: GroupByCriteria[]) => void;
  setFilters: (filters: FilterCriteria[]) => void;
  setLogicExpression: (expression: string | null) => void;
}

const FilterSection = ({
  availableFields,
  selectedFields,
  orderBy,
  onOrderByChange,
  groupBy,
  onGroupByChange,
  setFilters,
  setLogicExpression,
}: FilterSectionProps) => {
  const [picklistValues, setPicklistValues] = useState<
    Record<string, string[]>
  >({});
  const [showAddFilter, setShowAddFilter] = useState<boolean>(false);
  const { getPicklistValues } = useSalesforce();
  const { localFilters, addFilter, removeFilter, updateFilter } =
    useFilterManagement(setFilters, setLogicExpression);

  const loadPicklistValues = async (field: IncidentQueryField) => {
    try {
      if (field.Treat_as_Picklist__c && field.Values__c) {
        const values = getValuesFromField(field);
        setPicklistValues((prev) => ({
          ...prev,
          [field.Field_API_Name__c]: values,
        }));
        return;
      }

      const values = await getPicklistValues(field.Field_API_Name__c);
      const finalValues = field.Values__c
        ? values.filter((value) => getValuesFromField(field).includes(value))
        : values;

      setPicklistValues((prev) => ({
        ...prev,
        [field.Field_API_Name__c]: finalValues,
      }));
    } catch (error) {
      console.error(
        `Failed to load picklist values for ${field.Field_API_Name__c}:`,
        error
      );
    }
  };

  const handleFieldChange = async (filterId: string, fieldName: string) => {
    const newField = availableFields.find(
      (f) => f.Field_API_Name__c === fieldName
    );
    if (newField) {
      const updates: Partial<FilterCriteria> = { fieldName };

      if (isPicklistField(newField)) {
        updates.value = [];
        updates.operator = "IN";
        if (!picklistValues[fieldName]) {
          loadPicklistValues(newField);
        }
      }

      updateFilter(filterId, updates);
    }
  };

  const addPicklistValue = (filterId: string, value: string) => {
    const filter = localFilters.find((f) => f.id === filterId);
    if (filter) {
      const currentValues = Array.isArray(filter.value) ? filter.value : [];
      if (!currentValues.includes(value)) {
        updateFilter(filterId, { value: [...currentValues, value] });
      }
    }
  };

  const removePicklistValue = (filterId: string, valueToRemove: string) => {
    const filter = localFilters.find((f) => f.id === filterId);
    if (filter) {
      const currentValues = Array.isArray(filter.value) ? filter.value : [];
      updateFilter(filterId, {
        value: currentValues.filter((v) => v !== valueToRemove),
      });
    }
  };

  const availableGroupFields = availableFields.filter(
    (field) =>
      selectedFields.includes(field.Field_API_Name__c) &&
      GROUPABLE_FIELD_TYPES.includes(field.Field_Type__c as FieldType)
  );

  return (
    <div
      // className="h-[350px] space-y-4 p-4 md:h-[450px]"
      className="space-y-4 p-4"
      style={{
        backgroundColor: "#FFF",
        border: "1px solid lightgrey",
        overflowY: "scroll",
      }}
    >
      <div className="space-y-4 border-t pt-4">
        <OrderBySelector
          selectedFields={selectedFields}
          availableFields={availableFields}
          orderBy={orderBy}
          onOrderByChange={onOrderByChange}
        />

        <GroupBySelector
          selectedFields={selectedFields}
          availableFields={availableFields}
          groupBy={groupBy}
          onGroupByChange={onGroupByChange}
        />
      </div>

      <div className="space-y-4">
        {localFilters.map((filter, index) => {
          const selectedField = availableFields.find(
            (f) => f.Field_API_Name__c === filter.fieldName
          );

          const displayValue = Array.isArray(filter.value)
            ? filter.value.join(", ")
            : filter.value;

          return (
            <div
              key={filter.id}
              className="flex items-center justify-between gap-2 rounded bg-gray-50 p-2"
            >
              <div className="flex-1">
                <span className="font-medium">
                  {selectedField?.Label ?? filter.fieldName}
                </span>
                <span className="mx-2 text-gray-500">{filter.operator}</span>
                <span className="text-gray-700">
                  {displayValue || "(empty)"}
                </span>
              </div>
              <button
                onClick={() => removeFilter(filter.id)}
                className="text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}

        <div className="ml-4">
          <button
            onClick={() => {
              addFilter();
              setShowAddFilter(true);
            }}
            disabled={availableGroupFields.length === 0}
            className="w-full cursor-pointer rounded border-0 bg-purple-400 p-1 text-white-main hover:bg-purple-500 md:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Filter
          </button>
        </div>
      </div>

      {showAddFilter && (
        <FilterSectionModal
          showAddFilter={showAddFilter}
          setShowAddFilter={setShowAddFilter}
          localFilters={localFilters}
          availableFields={availableFields}
          availableGroupFields={availableGroupFields}
          picklistValues={picklistValues}
          handleFieldChange={handleFieldChange}
          updateFilter={updateFilter}
          removeFilter={removeFilter}
          addPicklistValue={addPicklistValue}
          removePicklistValue={removePicklistValue}
        />
      )}
    </div>
  );
};

export default FilterSection;
